import { FormEvent, useEffect, useState } from 'react';
import { authenticateByEmail, getAuthErrorMessage } from './auth.api';
import { AuthMode, AuthSession } from './auth.schemas';
import './AuthModal.css';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type AuthModalProps = {
  isOpen: boolean;
  mode: AuthMode;
  onClose: () => void;
  onModeChange: (mode: AuthMode) => void;
  onSuccess: (session: AuthSession) => void | Promise<void>;
};

export function AuthModal({ isOpen, mode, onClose, onModeChange, onSuccess }: AuthModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setError('');
    }
  }, [isOpen, mode]);

  if (!isOpen) {
    return null;
  }

  const isRegister = mode === 'register';
  const normalizedEmail = email.trim();

  const handleModeChange = (nextMode: AuthMode) => {
    setIsForgotMode(false);
    setError('');
    onModeChange(nextMode);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    if (!emailPattern.test(normalizedEmail)) {
      setError('Введите корректный email');
      return;
    }

    if (password.length < 8) {
      setError('Пароль должен быть не короче 8 символов');
      return;
    }

    setIsSubmitting(true);

    try {
      const session = await authenticateByEmail(mode, normalizedEmail, password);
      await onSuccess(session);
      setPassword('');
    } catch (requestError) {
      setError(getAuthErrorMessage(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-overlay" role="presentation" onMouseDown={onClose}>
      <section
        className="auth-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button className="auth-close" type="button" aria-label="Закрыть авторизацию" onClick={onClose}>
          x
        </button>

        <div className="auth-header">
          <span className="eyebrow compact">Личный кабинет</span>
          <h2 id="auth-title">{isForgotMode ? 'Восстановить пароль' : isRegister ? 'Создать аккаунт' : 'Войти в MifVPN'}</h2>
          <p>{isForgotMode ? 'Введите email, и мы отправим ссылку для сброса пароля.' : isRegister ? 'Создайте вход по почте и паролю.' : 'Введите почту и пароль, чтобы открыть кабинет.'}</p>
        </div>

        {!isForgotMode ? <div className="auth-tabs" role="tablist" aria-label="Режим авторизации">
          <button
            className={mode === 'login' ? 'active' : ''}
            type="button"
            role="tab"
            aria-selected={mode === 'login'}
            onClick={() => handleModeChange('login')}
          >
            Вход
          </button>
          <button
            className={mode === 'register' ? 'active' : ''}
            type="button"
            role="tab"
            aria-selected={mode === 'register'}
            onClick={() => handleModeChange('register')}
          >
            Регистрация
          </button>
        </div> : null}

        {!isForgotMode ? <div className="auth-telegram-note">
          <strong>Есть подписка в Telegram?</strong>
          <span>Войдите или зарегистрируйтесь. После этого можно перенести подписку из Telegram в этот кабинет.</span>
        </div> : null}

        {isForgotMode ? (
          <div className="auth-support-reset">
            <div className="auth-success">
              Обратитесь в нашу службу поддержки. Она поможет восстановить ваш пароль.
            </div>
            <a className="button button-primary auth-submit" href="https://t.me/mifrain" target="_blank" rel="noreferrer">
              Написать в поддержку
            </a>
            <button className="auth-link-button" type="button" onClick={() => {
              setIsForgotMode(false);
              setError('');
            }}>
              Вернуться ко входу
            </button>
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            <label>
              <span>Email</span>
              <input
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                inputMode="email"
                name="email"
                placeholder="Ваша почта"
                required
                spellCheck={false}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>

            <label>
              <span>Пароль</span>
              <input
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                minLength={8}
                name="password"
                placeholder="Ваш Пароль"
                required
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>

            {error ? <div className="auth-error">{error}</div> : null}

            <button className="button button-primary auth-submit" disabled={isSubmitting} type="submit">
              {isSubmitting ? 'Проверяем...' : isRegister ? 'Зарегистрироваться' : 'Войти'}
            </button>
            {!isRegister ? (
            <button className="auth-link-button" type="button" onClick={() => {
              setIsForgotMode(true);
              setError('');
            }}>
              Забыли пароль?
            </button>
            ) : null}
          </form>
        )}
      </section>
    </div>
  );
}
